import { useEffect, useRef } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

/**
 * VideoRenderer — renders a real `<video>` for `type: "video"` elements and
 * auto-registers it with the host MediaRegistry so the `mediaControl`
 * ActionFlow action (play / pause / seekPercent / seekRelative / setVolume /
 * setMuted / toggleFullscreen) imperatively drives the live element.
 *
 * Registration uses `RenderContext.registerMedia` (populated by AppV2 from
 * `hostServices.mediaRegistry`). The blockId is resolved from the nearest
 * `[data-cogo-block-id]` ancestor — AppV2 wraps each top-level block element
 * with it (App.tsx) — falling back to the element id for standalone renders.
 * When `registerMedia` is absent (headless/standalone), the element still
 * renders; only the imperative control is a no-op.
 *
 * The corpus pairs each `mediaControl` with a `setState` that drives the
 * reactive chrome (play/pause icon, progress bar); this renderer is only the
 * imperative side-effect target on the live `<video>`.
 */
export function VideoRenderer({ element }: RendererProps) {
  const { resolveSymbol, registerMedia } = useRenderContext()
  const ref = useRef<HTMLVideoElement>(null)

  const src = String(resolveSymbol(element.src ?? "") ?? "")
  const posterRaw = (element as { poster?: string }).poster
  const poster = posterRaw ? String(resolveSymbol(posterRaw) ?? "") : undefined
  const controls = (element as { controls?: boolean }).controls ?? true
  const muted = (element as { muted?: boolean }).muted ?? false

  useEffect(() => {
    const node = ref.current
    if (!node || !registerMedia) return
    const blockId =
      node.closest("[data-cogo-block-id]")?.getAttribute("data-cogo-block-id") || element.id
    return registerMedia(blockId, node)
  }, [registerMedia, element.id])

  return (
    <video
      ref={ref}
      id={element.id}
      src={src || undefined}
      poster={poster || undefined}
      controls={controls}
      muted={muted}
      autoPlay={element.autoplay ?? undefined}
      loop={element.loop ?? undefined}
      playsInline
      className={cn("cn-video", element.className)}
      data-atom-type="video"
    />
  )
}
